import type { PlayerShip } from "./types";
import { HP_PER_FERRITE, REPAIR_ORE, ferriteForHp } from "./repairEconomy";

// Persistent hull/shield damage (16.81, Don's direction). Before this,
// damage was session-only: expeditionScreen.ts rebuilt every ship at full
// HP on entry, so leaving and re-entering the Expedition View was a free
// full repair, and 16.80 had to leave station/drydock repair design-only
// because there was nothing left to bill. Damage now survives travel, and
// the ONLY three things that undo it are an activated Shield/Hull module,
// a Station drydock (credits + resources), and a Base repair bay
// (resources only).
//
// Stored as damage TAKEN, not current HP: a hull's max HP comes from the
// roster (16.16), so recording the delta means a save stays correct if a
// hull's stats are ever rebalanced, and an undamaged ship stores nothing.
export interface ShipDamage {
  hull: number;
  shield: number;
}

// Keyed by ship INSTANCE id (state.currentShipInstanceId / FleetShip.id),
// never by ship name -- two identically-named hulls in one fleet take
// damage independently, the same reason weaponUpgrades keys this way.
export type ShipDamageMap = Record<string, ShipDamage>;

export function damageFor(map: ShipDamageMap, instanceId: string | null): ShipDamage {
  if (!instanceId) return { hull: 0, shield: 0 };
  const d = map[instanceId];
  return d ? { hull: Math.max(0, d.hull), shield: Math.max(0, d.shield) } : { hull: 0, shield: 0 };
}

export function isDamaged(d: ShipDamage): boolean {
  return d.hull > 0.01 || d.shield > 0.01;
}

// An undamaged ship is deleted rather than stored as a pair of zeroes --
// keeps saves from accumulating an entry per ship ever flown.
export function setDamage(map: ShipDamageMap, instanceId: string | null, damage: ShipDamage): void {
  if (!instanceId) return;
  if (!isDamaged(damage)) {
    delete map[instanceId];
    return;
  }
  map[instanceId] = { hull: Math.max(0, damage.hull), shield: Math.max(0, damage.shield) };
}

// Records a ship's live HP as damage. The Expedition View works in current
// HP; everything persisted works in damage taken.
export function recordCurrentHp(
  map: ShipDamageMap,
  instanceId: string | null,
  current: { hull: number; hullMax: number; shield: number; shieldMax: number }
): void {
  setDamage(map, instanceId, {
    hull: Math.max(0, current.hullMax - current.hull),
    shield: Math.max(0, current.shieldMax - current.shield)
  });
}

export type RepairVenue = "station" | "base";

// A Station drydock's credit fee for repairing a hull from zero to full,
// as a fraction of that hull's own Shipyard price. Solved backward from
// intent (CLAUDE.md): flying home to your own Base instead of paying a
// faction yard has to be a real choice, so the fee needs to be felt --
// but stranding a captain who just barely survived a fight would be worse
// than the fight. At 15%, a wrecked Marauder I costs 89 CR against its
// 590 CR hull and against 60-150 CR T1 encounter rewards: roughly one
// fight's pay to undo one fight's damage, and never a reason to sell the
// ship. Scales across all 96 hulls automatically, since it is priced off
// each hull's own cost rather than a flat table.
// 16.92 divided this by 100 when hull prices were multiplied by 100, so the
// absolute repair bill is unchanged. The fee is a fraction of costCr, so a bare
// hull reprice would have made repairs 100x more expensive as a side effect --
// which nobody asked for, and which would have made any real fight unaffordable
// (a half-wrecked T4 would cost more to repair than the median combat mission
// pays, several times over).
export const STATION_REPAIR_FEE_FRACTION = 0.0015;

export interface RepairQuote {
  hullHp: number;
  shieldHp: number;
  credits: number;
  ferrite: number;
}

// What it costs to make a ship whole again.
//
// Materials are the same Ferrite at the same 16.80 rate the field Repair
// module burns -- a hull plate costs what a hull plate costs, whether the
// crew welds it on under fire or a yard arm does it docked. Shields never
// cost materials at either venue: 16.80 already established shield regen
// as powered, not material.
//
// A Base charges no credits at all (Don's direction): it's your land, your
// crew, and you already paid 10,000 CR for the lease plus the lot -- the
// yard fee is exactly what owning a Base buys you out of.
export function repairQuote(ship: PlayerShip, damage: ShipDamage, venue: RepairVenue): RepairQuote {
  const hullHp = Math.max(0, Math.min(damage.hull, ship.hullHp));
  const shieldHp = Math.max(0, Math.min(damage.shield, ship.shieldHp));
  const totalEhp = ship.hullHp + ship.shieldHp;
  const damageFraction = totalEhp > 0 ? (hullHp + shieldHp) / totalEhp : 0;
  return {
    hullHp,
    shieldHp,
    ferrite: ferriteForHp(hullHp),
    credits:
      venue === "station" ? Math.ceil(ship.costCr * STATION_REPAIR_FEE_FRACTION * damageFraction) : 0
  };
}

export function canAffordRepair(quote: RepairQuote, credits: number, ferriteAboard: number): boolean {
  return credits >= quote.credits && ferriteAboard >= quote.ferrite - 0.0001;
}

// Human-readable cost line, shared by both venues so a Base and a Station
// never describe the same charge two different ways.
export function repairCostLabel(quote: RepairQuote): string {
  const parts: string[] = [];
  if (quote.credits > 0) parts.push(`${quote.credits.toLocaleString()} CR`);
  if (quote.ferrite > 0) parts.push(`${quote.ferrite.toFixed(1)} ${REPAIR_ORE}`);
  return parts.length ? parts.join(" + ") : "free";
}

export { HP_PER_FERRITE, REPAIR_ORE };
